/**
 * TestCaseFilterBar — compact filter strip above the test case list.
 *
 * Reads and writes the Zustand `filters` slice.
 * Supports filtering by: Tags, Case Type, Severity.
 *
 * Each filter type uses a small pill toggle UI — click to activate/deactivate.
 * Active filters are highlighted in blue. A "Clear" button removes all active filters.
 */

import { useState, useRef, useEffect, useMemo } from 'react';
import { Filter, ChevronDown, X } from 'lucide-react';
import { useTestCasesStore } from '../../../store/useTestCasesStore';
import type { ITestCaseRow } from '../types';
import {
    ALL_SEVERITIES, ALL_CASE_TYPES,
    SEVERITY_LABELS, CASE_TYPE_LABELS,
    type TestSeverity, type TestCaseType,
} from '../types';

interface TestCaseFilterBarProps {
    allTestCases: ITestCaseRow[];
}

export function TestCaseFilterBar({ allTestCases }: TestCaseFilterBarProps) {
    const filters      = useTestCasesStore((s) => s.filters);
    const setFilters   = useTestCasesStore((s) => s.setFilters);
    const resetFilters = useTestCasesStore((s) => s.resetFilters);

    const [openPanel, setOpenPanel] = useState<'severity' | 'caseType' | 'tags' | null>(null);
    const panelRef = useRef<HTMLDivElement>(null);

    const activeCount =
        (filters.severity?.length ?? 0) +
        (filters.caseType?.length ?? 0) +
        (filters.tags?.length ?? 0);

    const hasFilters = activeCount > 0;

    // Close panel on outside click
    useEffect(() => {
        function handleClick(e: MouseEvent) {
            if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
                setOpenPanel(null);
            }
        }
        if (openPanel) document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [openPanel]);

    // Distinct tags from the currently loaded test cases
    const availableTags = useMemo(() => {
        const set = new Set<string>();
        allTestCases.forEach((tc) => tc.tags?.forEach((t) => set.add(t)));
        return [...set].sort();
    }, [allTestCases]);

    function toggleSeverity(s: TestSeverity) {
        const current = filters.severity ?? [];
        const next = current.includes(s)
            ? current.filter((v) => v !== s)
            : [...current, s];
        setFilters({ severity: next.length ? next : undefined });
    }

    function toggleCaseType(ct: TestCaseType) {
        const current = filters.caseType ?? [];
        const next = current.includes(ct)
            ? current.filter((v) => v !== ct)
            : [...current, ct];
        setFilters({ caseType: next.length ? next : undefined });
    }

    function toggleTag(tag: string) {
        const current = filters.tags ?? [];
        const next = current.includes(tag)
            ? current.filter((v) => v !== tag)
            : [...current, tag];
        setFilters({ tags: next.length ? next : undefined });
    }

    return (
        <div data-testid="test-case-filter-bar" className="flex items-center gap-1.5 px-3 py-1.5 border-b border-slate-100 dark:border-gh-border-dark bg-slate-50/70 dark:bg-slate-900/20 flex-shrink-0 flex-wrap relative" ref={panelRef}>
            {/* Label */}
            <span className="flex items-center gap-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wider flex-shrink-0">
                <Filter size={10} /> Filters
            </span>

            {/* Severity dropdown */}
            <FilterDropdown
                label="Severity"
                active={(filters.severity?.length ?? 0) > 0}
                activeCount={filters.severity?.length}
                isOpen={openPanel === 'severity'}
                onToggle={() => setOpenPanel(openPanel === 'severity' ? null : 'severity')}
            >
                <div className="flex flex-col gap-0.5 p-1">
                    {ALL_SEVERITIES.map((s) => (
                        <button
                            key={s}
                            onClick={() => toggleSeverity(s)}
                            className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs text-left transition-colors
                                ${filters.severity?.includes(s)
                                    ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 font-semibold'
                                    : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                                }`}
                        >
                            {filters.severity?.includes(s) && <span className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />}
                            {SEVERITY_LABELS[s]}
                        </button>
                    ))}
                </div>
            </FilterDropdown>

            {/* Case Type dropdown */}
            <FilterDropdown
                label="Case Type"
                active={(filters.caseType?.length ?? 0) > 0}
                activeCount={filters.caseType?.length}
                isOpen={openPanel === 'caseType'}
                onToggle={() => setOpenPanel(openPanel === 'caseType' ? null : 'caseType')}
            >
                <div className="flex flex-col gap-0.5 p-1">
                    {ALL_CASE_TYPES.map((ct) => (
                        <button
                            key={ct}
                            onClick={() => toggleCaseType(ct)}
                            className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs text-left transition-colors
                                ${filters.caseType?.includes(ct)
                                    ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 font-semibold'
                                    : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                                }`}
                        >
                            {filters.caseType?.includes(ct) && <span className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />}
                            {CASE_TYPE_LABELS[ct]}
                        </button>
                    ))}
                </div>
            </FilterDropdown>

            {/* Tags dropdown */}
            {availableTags.length > 0 && (
                <FilterDropdown
                    label="Tags"
                    active={(filters.tags?.length ?? 0) > 0}
                    activeCount={filters.tags?.length}
                    isOpen={openPanel === 'tags'}
                    onToggle={() => setOpenPanel(openPanel === 'tags' ? null : 'tags')}
                >
                    <div className="flex flex-col gap-0.5 p-1 max-h-48 overflow-y-auto">
                        {availableTags.map((tag) => (
                            <button
                                key={tag}
                                onClick={() => toggleTag(tag)}
                                className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs text-left transition-colors
                                    ${filters.tags?.includes(tag)
                                        ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 font-semibold'
                                        : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                                    }`}
                            >
                                {filters.tags?.includes(tag) && <span className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />}
                                {tag}
                            </button>
                        ))}
                    </div>
                </FilterDropdown>
            )}

            {/* Active filter pills — show what's active */}
            {filters.severity?.map((s) => (
                <ActivePill key={`sev-${s}`} label={SEVERITY_LABELS[s]} onRemove={() => toggleSeverity(s)} />
            ))}
            {filters.caseType?.map((ct) => (
                <ActivePill key={`ct-${ct}`} label={CASE_TYPE_LABELS[ct]} onRemove={() => toggleCaseType(ct)} />
            ))}
            {filters.tags?.map((t) => (
                <ActivePill key={`tag-${t}`} label={t} onRemove={() => toggleTag(t)} />
            ))}

            {/* Clear all */}
            {hasFilters && (
                <button
                    data-testid="filter-bar-clear-button"
                    aria-label="Clear all filters"
                    onClick={resetFilters}
                    className="ml-auto flex items-center gap-1 text-[10px] text-rose-500 hover:text-rose-700 dark:hover:text-rose-400 transition-colors flex-shrink-0"
                >
                    <X size={10} /> Clear all
                </button>
            )}
        </div>
    );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function FilterDropdown({
    label,
    active,
    activeCount,
    isOpen,
    onToggle,
    children,
}: {
    label: string;
    active: boolean;
    activeCount?: number;
    isOpen: boolean;
    onToggle: () => void;
    children: React.ReactNode;
}) {
    return (
        <div className="relative flex-shrink-0">
            <button
                data-testid={`filter-dropdown-${label.toLowerCase()}`}
                aria-expanded={isOpen}
                onClick={onToggle}
                className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-medium transition-colors border
                    ${active
                        ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700'
                        : 'bg-white dark:bg-gh-bg-dark text-slate-600 dark:text-slate-400 border-slate-200 dark:border-gh-border-dark hover:bg-slate-100 dark:hover:bg-slate-700/50'
                    }`}
            >
                {label}
                {activeCount ? <span className="ml-0.5 text-[9px] font-bold text-blue-500">({activeCount})</span> : null}
                <ChevronDown size={9} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <div className="absolute top-full left-0 mt-1 z-50 bg-white dark:bg-slate-800 border border-slate-200 dark:border-gh-border-dark rounded-xl shadow-lg min-w-[140px]">
                    {children}
                </div>
            )}
        </div>
    );
}

function ActivePill({ label, onRemove }: { label: string; onRemove: () => void }) {
    return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 flex-shrink-0">
            {label}
            <button aria-label={`Remove ${label} filter`} onClick={onRemove} className="hover:text-blue-900 dark:hover:text-blue-100 transition-colors">
                <X size={9} />
            </button>
        </span>
    );
}
